import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';
import _ from 'lodash';
import classNames from 'classnames';

import { RootState, select } from 'src/configs/store';
import Icon from 'src/components/Icon/Icon';
import TypeCodes from '../TypeCodesModal/TypeCodes';
import DraggableDialog from 'src/components/DraggableDialog/DraggableDialog';
import { generateCalculationsPayload } from 'src/utils/lottoHelper';
import ToolTip from 'src/components/ToolTip/ToolTip';
import SmartInput from 'src/SmartInput/SmartInput';
import NumberInput from './NumberInput/NumberInput';
import FastModeHistory from 'src/components/NumbersBetting/FastModeHistory/FastModeHistoryLotto';
import UseActiveElement from 'src/hooks/useActiveElement';
import { getNumberOfCombinations } from 'src/utils/utils';

const NORMALMARKET = 500;
const MAINBONUSMARKET = 600;
const BONUSMARKET = 700;
const MAINPLUSBONUSMARKET = 800;

interface NumbersFastModeProps {
  coupon?: number,
  card?: string,
  selectedEvents?:{ code?: number, scheduled: number }[],
}

function NumbersFastMode({ coupon=null, card=null, selectedEvents=[] } : NumbersFastModeProps) {
  const [showModal, setShowModal] = useState(false);
  const [focus, setFocus]= useState('cardCodes');
  const [cardCodes, setCardCodes] = useState([]);
  const [betCodes, setBetCodes] = useState([]);
  const [selectedMarket, setSelectedMarket] = useState(null);
  const [clear, setClear] = useState(false);
  const [lastSelection, setLastSelection] = useState(false);
  const [lastValues, setLastValues] = useState(['']);
  const [marketDefaultValue, setMarketDefaultValue] = useState('');
  const [shouldScroll, setShouldScroll] = useState(false);
  const [stake, setStake] = useState('');

  const [defaultValue, setDefaultValue] = useState('');
  const focusedElement = UseActiveElement();

  const { t } = useTranslation();
  const { retailErrors, retailValues } = useSelector((state: RootState) => state.racing);
  const { lottoErrors,
    lottoValues,
    fastModeRules,
    singleNumberEvent,
    valuesReset,
    singleNumberEvent: { tournamentMarkets } } = useSelector((state: RootState) => state.lotto);
  const { bets } = useSelector((state: RootState) => state.betslip);
  const { stakingRules } = useSelector((state: RootState) => state.content);
  const { typeOfCode } = useSelector((state: RootState) => state.peripherals);
  const selectedSlip = useSelector(select.betslip.selectedSlip);

  const dispatch = useDispatch();

  const firstFocus = useRef(null);
  const secondFocus = useRef(null);
  const lastFocus = useRef(null);
  const stakeRef = useRef(null);

  const getCombinations = async (eventCode, marketCode, orderedPositions, selectedSelectionType, selectedMarket, selectedHorsePosition) => {
    const payload = generateCalculationsPayload(
      _.map(_.filter(orderedPositions, (number) => number.type !== 'bonus'), (number) => number.number),
      [],
      selectedMarket,
      eventCode,
      0,
      selectedHorsePosition,
      selectedSelectionType
    );

    return await dispatch.lotto.permutations(payload);
  };

  useEffect(() => {
    setDefaultValue('');
    setMarketDefaultValue('');
    firstFocus.current.focus();
  }, [valuesReset]);

  const checkMarketName = (market, spec1, specValue, spec2=null, specValue2=null) => {
    if (market === 800){
      return `${ spec1 } ${ specValue } + ${ spec2 } ${ specValue2 }`;
    } else if (market === 500){
      return `${ spec1 } ${ specValue } Classic`;
    } else if (market === 600){
      return `${ spec1 } ${ specValue } Main With Bonus`;
    }
    return `${ spec1 } ${ specValue }`;
  };

  const getCodes = async () => {
    const eventCodes = await dispatch.events.getEventCodes(4);
    const marketCodes = await dispatch.events.getNumberBetCodes(4);
    if (eventCodes){
      setCardCodes(eventCodes);
    }

    if (marketCodes){
      setBetCodes(marketCodes);
    }
  };

  useEffect(() => {
    getCodes();
  }, []);

  const checkColors = () => {
    if (card || coupon){
      return singleNumberEvent.numberInfos?.regular?.colors || undefined;
    }
    return fastModeRules.regular?.colors || undefined;
  };

  useEffect(() => {
    if (coupon || card){
      const event = _.find(cardCodes, { [typeOfCode === 'cardCode' ? 'couponCode' : 'couponCardCode']: typeOfCode === 'cardCode' ? coupon : card });

      if (event){
        dispatch.lotto.changeValues({ key: 'betCode', value: typeOfCode === 'cardCode' ? card.toString() : coupon.toString() });
        setDefaultValue(`${ typeOfCode === 'cardCode' ? card : coupon } - ${ event.name
        } ${ selectedEvents.length > 1 ? `+${ selectedEvents.length -1 }` : '' }`);
      }
    }
    return (() =>  dispatch.lotto.changeValues({ key: 'betCode', value: '' }));
  }, [coupon, card, cardCodes, selectedEvents.length]);

  // eslint-disable-next-line no-unused-vars
  const checkIfValidInput = (code) => {
    const codeSearch =  /\b(?=(?:[1-9]*[A-I]){0,4}(?![1-9]*[A-I]))(?=(?:[A-I]*[1-9]){0,4}(?![A-I]*[1-9]))[A-I1-9]+\b/.test(code);
    const checkIfRepeated = _.uniq(code).length !== code.length;

    if (!codeSearch || code.length > 8 || checkIfRepeated){
      dispatch.racing.changeErrors({  value:'betcode', error:t('betCodeSearch.invalidFormat') });
    }
  };

  const splitNumbers = () => {
    const divided = lottoValues.numbers.split('+').map(s => s.match(new RegExp('.{1,' + 2 + '}', 'g')));
    return divided;
  };

  const lottoNumbers = splitNumbers();

  const checkCombinations = async (eventInfo, market, selectedNumbers) => {
    let numbersSelected = lottoNumbers[0];

    if (!lottoNumbers[0]){
      numbersSelected = lottoNumbers[1];
    } else if (lottoNumbers[0] && lottoNumbers[1]){
      const combinations =  await getCombinations(eventInfo.code,
        market,
        selectedNumbers,
        _.filter(selectedNumbers, { type: 'bonus' }),
        market, market.predictionCount);
      return combinations.bets[0].prediction.permutationsCount;
    }

    return getNumberOfCombinations(numbersSelected, market.predictionCount);
  };

  const handleStake = async (e) => {
    setStake(e.target.value);
    await dispatch.lotto.changeValues({ key: e.target?.name, value: e.target.value });

    if (retailErrors[e.target?.name] !== ''){
      dispatch.lotto.changeErrors({ value:'', error: '' });
    }
  };

  const haveEmptyFields = () => {
    return _.isEmpty(lottoValues.betCode)
      ||  _.isEmpty(lottoValues.numbers);
  };

  const handleEmptyBetCode = async (info) => {
    dispatch.lotto.resetFastModeErrors();

    let typeOfMarket = MAINBONUSMARKET;

    if (info?.error || !info){
      // Check if node retrieves error and stop if it has error.
      if (info?.error === 'INVALID_CODE'){
        dispatch.lotto.changeErrors({ value:'betcode', error:t('racingDetail.invalidEvent') });
        return ;
      }
    }

    const bonus = lottoNumbers[1]?.length || 0;
    const numbers = lottoNumbers[0]?.length || 0;
    const selectedNumbers = [];
    const infos = info.tournament.numberInfos;

    if (!lottoNumbers[0] && lottoNumbers.length === 2){
      typeOfMarket = NORMALMARKET;
    } else if (lottoNumbers.length === 2 && !lottoNumbers[1]){
      typeOfMarket = BONUSMARKET;
    } else if (lottoNumbers.length === 2 && lottoNumbers[0] && lottoNumbers[1]){
      typeOfMarket = MAINPLUSBONUSMARKET;
    }

    const markets = _.filter(info.markets.markets, { typeCode: typeOfMarket });
    if (markets.length === 0){
      dispatch.lotto.changeErrors({ value:'betType', error:t('racingDetail.invalidMarket') });
      return;
    }
    const predictionCount = bonus === 0 && numbers !== 0 ? numbers : bonus;
    let market = _.find(markets, { predictionCount: predictionCount });
    if (typeOfMarket === MAINPLUSBONUSMARKET){
      market =  _.find(markets, { specifiers:[
        { value: numbers.toString(), name: 'numbers' },
        { value: bonus.toString(), name:'bonus' }
      ]
      });
    }

    if (!market) {
      dispatch.lotto.changeErrors({ value:'betType', error:t('racingDetail.invalidMarket') });
      return;
    }

    if (market.typeCode === 800){
      if (!lottoNumbers[1] || !lottoNumbers[0] || lottoNumbers[0].length === 0 || lottoNumbers[1].length === 0){
        dispatch.lotto.changeErrors({ value:'numbers', error:t('numbers.invalidBonusDetail') });
        return ;
      }
      const repeatedNumbers = lottoNumbers[0].filter(number => lottoNumbers[1].includes(number));
      if (infos.bonus.sameBallset && repeatedNumbers.length > 0){
        dispatch.lotto.changeErrors({ value:'numbers', error:t('numbers.bonusNumberFromSameDraw') });
        return ;
      }
    }

    if (lottoNumbers[0]){
      for (const number of lottoNumbers[0]){
        const type = typeOfMarket !== BONUSMARKET || infos.bonus.sameBallset ? 'ball' : 'star';
        const keyToCheck = type === 'ball' ? 'regular' : 'bonus';

        if ((+number > infos[keyToCheck].balls || +number < infos[keyToCheck].startNumber)){
          dispatch.lotto.changeErrors({ value:'numbers', error:t('numbers.bonusNotOnDrum') });
          return ;
        }
        selectedNumbers.push({ number: parseInt(number, 10), isBanker: false, type, color: infos.regular.colors[+number -1] });
      }
    }
    if (lottoNumbers[1]){
      for (const number of lottoNumbers[1]){
        const type = typeOfMarket === MAINPLUSBONUSMARKET ? 'bonus' : 'ball';
        const keyToCheck = infos.bonus.sameBallset || typeOfMarket !== MAINPLUSBONUSMARKET? 'regular' : 'bonus';

        if ((+number > infos[keyToCheck].balls || +number < infos[keyToCheck].startNumber)){
          dispatch.lotto.changeErrors({ value:'numbers', error:t('numbers.bonusNotOnDrum') });
          return ;
        }
        selectedNumbers.push({ number: parseInt(number, 10), isBanker: false, type, color: infos.regular.colors[+number -1] });
      }
    }

    if ((lottoValues.stake && +(lottoValues.stake) > 0) || (stake && +stake > 0)){
      dispatch.content.changeFastModeDefaultStake(+(lottoValues.stake));
    }
    const newPayload = [];
    const payload = {
      eventCode: info.code,
      eventName: info.tournament.name,
      providerCode: 1,
      odds: market.outcomes[0].value,
      marketOutcomeCode: market.outcomes[0].code,
      marketCode: market.code,
      marketName: market.name,
      jsonLabel: selectedNumbers,
      tournamentName: info.tournament.name,
      datetime: info.scheduled * 1000,
      sport: 'lotto',
      type: 'numbers',
      country: info.tournamentGroup.name,
      selectedNumbers: selectedNumbers,
      permutationsCount: 1,
      productType: 4,
      numbersToPredictCount: market.predictionCount,
      stake: stake || +(lottoValues.stake) || stakingRules.lotto.single.fastModeDefault,
      repetitionsCount: 1,
      sameBallSet: infos.bonus?.sameBallset,
      market
    };
    setClear(true);
    const setMarketName = checkMarketName(market.typeCode, market.specifiers[0].value, market.specifiers[0].name,
      market.specifiers[1]?.value, market.specifiers[1]?.name);

    if (selectedEvents.length > 0){

      for (const newEvent of selectedEvents){
        const newMarkets = _.find(tournamentMarkets, { eventCode: newEvent.code });
        const newMarket = _.find(newMarkets.markets, { name: market.name, betCode: market.betCode, status: market.status });

        if (!newMarket){
          dispatch.lotto.changeErrors({ value:'betType', error:t('racingDetail.invalidMultipleMarkets', {
            market: market.name, event: newEvent.code }) });
          return ;
        }

        newPayload.push({
          eventCode: newEvent.code,
          eventName: info.tournament.name,
          providerCode: 1,
          odds: newMarket['outcomes'][0].value,
          marketOutcomeCode: newMarket['outcomes'][0].code,
          marketCode: newMarket['code'],
          marketName: newMarket['name'],
          jsonLabel: selectedNumbers,
          tournamentName: info.tournament.name,
          datetime: newEvent.scheduled * 1000,
          sport: 'lotto',
          type: 'numbers',
          country: info.tournamentGroup.name,
          selectedNumbers: selectedNumbers,
          permutationsCount: 1,
          productType: 4,
          numbersToPredictCount: market.predictionCount,
          stake: stake || +(lottoValues.stake) || stakingRules.lotto.single.fastModeDefault,
          repetitionsCount: 1,
          sameBallSet: infos.bonus?.sameBallset,
          market
        });
      }
    }

    dispatch.lotto.addNumberToFastInputHistory({ ...payload, marketName: setMarketName, datetime: (new Date).getTime() });
    await dispatch.betslip.addBets(newPayload.length > 0 ? newPayload : [payload]);
  };

  const handleLottoSubmit = async (e) => {
    e.preventDefault();
    dispatch.lotto.resetFastModeErrors();
    if (selectedSlip.stakeSelection !== 'differentStake'){
      await dispatch.betslip.changeStakeSelection('differentStake');
    }

    if (_.isEmpty(lottoValues.betCode) || _.isEmpty(lottoValues.numbers)){
      return;
    } else if (_.isEmpty(lottoValues.betType) && !_.isEmpty(lottoValues.betCode) && !_.isEmpty(lottoValues.numbers)){
      const eventInfo = await dispatch.events.fastInput({ productType: 4, couponCard: lottoValues.betCode, betType: lottoValues.betType });
      handleEmptyBetCode(eventInfo);
      return;
    }

    let race = retailValues.race;
    if (race === 'CR') {
      race = '0';
    }

    const eventInfo = await dispatch.events.fastInput({ productType: 4, race, couponCard: lottoValues.betCode, betType: lottoValues.betType });

    if (eventInfo?.error || !eventInfo){
      // Check if node retrieves error and stop if it has error.
      if (eventInfo?.error === 'INVALID_MARKET'){
        dispatch.lotto.changeErrors({ value:'betType', error:t('racingDetail.invalidMarket') });
      } else {
        dispatch.lotto.changeErrors({ value:'betcode', error:t('racingDetail.invalidEvent') });
      }
      return;
    }

    const market = eventInfo.markets.markets[0];
    const infos = eventInfo.tournament.numberInfos;
    const selectedNumbers = [];

    if (market.typeCode !== 800){
      if (lottoNumbers.length === 1){
        if (market.predictionCount > lottoNumbers[0].length){
          dispatch.lotto.changeErrors({ value:'numbers', error:t('numbers.minimumRequired', {
            market: market.name,
            minimum: market.predictionCount,
            selected: lottoNumbers[0].length
          }) });
          return ;
        }
        for (const number of lottoNumbers[0]){
          let type = 'ball';

          if (market.typeCode === 700 && !infos.bonus.sameBallset){
            type = 'star';
          }

          if (((market.typeCode !== 700 || !infos.bonus.sameBallset) && (+number > infos.regular.balls || +number < infos.regular.startNumber))
          || (market.typeCode === 700 && !infos.bonus.sameBallset) && (+number > infos.bonus.balls || +number < infos.bonus.startNumber)
          ){
            dispatch.lotto.changeErrors({ value:'numbers', error:t('numbers.notOnDrum') });
            return ;
          }
          selectedNumbers.push({ number: parseInt(number, 10), isBanker: false, type, color: infos.regular.colors[+number -1] });
        }
      } else {
        dispatch.lotto.changeErrors({ value:'numbers', error:t('numbers.notBonusMarket') });
        return ;
      }
    } else if (market.typeCode === 800){
      if (!lottoNumbers[1] || !lottoNumbers[0] || lottoNumbers[0].length === 0 || lottoNumbers[1].length === 0){
        dispatch.lotto.changeErrors({ value:'numbers', error:t('numbers.invalidBonusDetail') });
        return ;
      }
      const repeatedNumbers = lottoNumbers[0].filter(number => lottoNumbers[1].includes(number));
      if (infos.bonus.sameBallset && repeatedNumbers.length > 0){
        dispatch.lotto.changeErrors({ value:'numbers', error:t('numbers.bonusNumberFromSameDraw') });
        return ;
      }

      const minimumBonus = +(market.specifiers[1].value);
      const minimumNumbers = +(market.specifiers[0].value);

      if (minimumNumbers > lottoNumbers[0].length){
        dispatch.lotto.changeErrors({ value:'numbers', error:t('numbers.minimumRequired', {
          market: market.name,
          minimum: minimumNumbers,
          selected: lottoNumbers[0].length
        }) });
        return ;
      } else if (minimumBonus > lottoNumbers[1].length){
        dispatch.lotto.changeErrors({ value:'numbers', error:t('numbers.minimumRequiredBonus', {
          market: market.name,
          minimum: minimumBonus,
          selected: lottoNumbers[1].length
        }) });
        return ;
      }


      for (const number of lottoNumbers[0]){
        const type = 'ball';
        if ((+number > infos.regular.balls || +number < infos.regular.startNumber)){
          dispatch.lotto.changeErrors({ value:'numbers', error:t('numbers.notOnDrum') });
          return ;
        }
        selectedNumbers.push({ number: parseInt(number, 10), isBanker: false, type, color: infos.regular.colors[+number -1] });
      }

      if (!lottoNumbers[1] || lottoNumbers[1].length === 0){
        dispatch.racing.changeErrors({ value:'numbers', error:t('racingDetail.invalidNumber') });
        return ;
      }

      for (const number of lottoNumbers[1]){
        const type = 'bonus';
        const keyToCheck = infos.bonus.sameBallset ? 'regular' : 'bonus';

        if ((+number > infos[keyToCheck].balls || +number < infos[keyToCheck].startNumber)){
          dispatch.lotto.changeErrors({ value:'numbers', error:t('numbers.bonusNotOnDrum') });
          return ;
        }

        selectedNumbers.push({ number: parseInt(number, 10), isBanker: false, type, color: infos.regular.colors[+number -1] });
      }
    }

    if (lottoValues.stake && +(lottoValues.stake) > 0){
      dispatch.content.changeFastModeDefaultStake(+(lottoValues.stake));
    }
    const newPayload = [];

    const payload = {
      eventCode: eventInfo.code,
      eventName: eventInfo.tournament.name,
      providerCode: 1,
      odds: market.outcomes[0].value,
      marketOutcomeCode: market.outcomes[0].code,
      marketCode: market.code,
      marketName: market.name,
      tournamentName: eventInfo.tournament.name,
      datetime: eventInfo.scheduled * 1000,
      sport: 'lotto',
      type: 'numbers',
      country: eventInfo.tournamentGroup.name,
      selectedNumbers: selectedNumbers,
      jsonLabel: selectedNumbers,
      permutationsCount: await checkCombinations(eventInfo, market, selectedNumbers),
      productType: 4,
      numbersToPredictCount: market.predictionCount,
      stake: stake || +lottoValues.stake || stakingRules.lotto.single.fastModeDefault,
      repetitionsCount: 1,
      sameBallSet: infos.bonus?.sameBallset,
      market
    };

    const setMarketName = checkMarketName(market.typeCode, market.specifiers[0].value, market.specifiers[0].name,
      market.specifiers[1]?.value, market.specifiers[1]?.name);

    if (selectedEvents.length > 0){
      for (const newEvent of selectedEvents){

        const newMarkets = _.find(tournamentMarkets, { eventCode: newEvent.code });
        const newMarket = _.find(newMarkets.markets, { name: market.name, betCode: market.betCode, status: market.status });

        if (!newMarket){
          dispatch.lotto.changeErrors({ value:'betType', error:t('racingDetail.invalidMultipleMarkets', {
            market: market.name, event: newEvent.code }) });
          return ;
        }

        newPayload.push({
          eventCode: newEvent.code,
          eventName: eventInfo.tournament.name,
          providerCode: 1,
          odds: newMarket['outcomes'][0].value,
          marketOutcomeCode: newMarket['outcomes'][0].code,
          marketCode: newMarket['code'],
          marketName: newMarket['name'],
          tournamentName: eventInfo.tournament.name,
          datetime: eventInfo.scheduled * 1000,
          sport: 'lotto',
          type: 'numbers',
          country: eventInfo.tournamentGroup.name,
          selectedNumbers: selectedNumbers,
          jsonLabel: selectedNumbers,
          permutationsCount: await checkCombinations(eventInfo, market, selectedNumbers),
          productType: 4,
          numbersToPredictCount: market.predictionCount,
          stake: stake || +lottoValues.stake || stakingRules.lotto.single.fastModeDefault,
          repetitionsCount: 1,
          sameBallSet: infos.bonus?.sameBallset,
          market
        });
      }
    }

    dispatch.lotto.addNumberToFastInputHistory({ ...payload, marketName: setMarketName, datetime: (new Date).getTime() });
    setClear(true);
    await dispatch.betslip.addBets(newPayload.length > 0 ? newPayload : [payload]);
  };

  useEffect(() => {
    const isNum = /[^\d+]+$/.test(lottoValues.numbers);
    if (lottoNumbers[0] && lottoNumbers[0].some((val, i) => lottoNumbers[0].indexOf(val) !== i) ||
        lottoNumbers[1] && lottoNumbers[1].some((val, i) => lottoNumbers[1].indexOf(val) !== i) || isNum){
      dispatch.lotto.changeErrors({ value:'numbers', error:t('racingDetail.invalidNumber') });
    }
  }, [lottoValues.numbers]);

  const changeFocus = useCallback(() => {
    setShowModal(false);
    if (focus === 'cardCodes'){
      setFocus('betCodes');
    } else {
      setFocus('cardCodes');
    }
    setShowModal(true);
  }, [focus]);

  const tabFunction = useCallback((e) => {
    if (e.key === '+'){
      e.preventDefault();
      handleLottoSubmit(e);
    }

    if (e.key === '*'){
      e.preventDefault();
      dispatch.lotto.resetRetailValues();
      dispatch.lotto.resetLottoFastModeHistory();
      dispatch.lotto.activateResetNumbers();
    }

    if (e.key === '/'){
      e.preventDefault();
      setMarketDefaultValue('');
      dispatch.lotto.changeValues({ key: 'betType', value: '' });
      setSelectedMarket(null);
    }

    if (e.key === 'Enter' && focusedElement.className.includes('focusable')){
      e.preventDefault();
      stakeRef.current.focus();
      if (stakeRef.current.value !== ''){
        stakeRef.current.select();
      }
    }

    if (e.key === 'I' && e.shiftKey){
      e.preventDefault();
      if (!showModal){
        setShowModal(true);
      }
    } else if (e.key === 'F4'){

      if (!_.isEqual(bets, [])){
        dispatch.betslip.submitBets(true);
      }
    } else if (e.key === '-'){
      setClear(true);
    } else if ((e.key === 'R' || e.key === 'r') && e.shiftKey){
      e.preventDefault();
      setLastSelection(true);
    } else if ((e.key === 'ArrowRight')){
      const inputs = document.getElementsByClassName('focusable');
      const selectedIndex = _.findIndex(inputs, (input) => input === focusedElement);

      if (selectedIndex >= 0){
        if (inputs[selectedIndex +1]){
          (inputs[selectedIndex +1] as HTMLElement)?.focus();
        } else {
          (inputs[0] as HTMLElement)?.focus();
        }
      } else if (document.activeElement.className.includes('--stake')){
        (inputs[inputs.length -1] as HTMLElement)?.focus();
      }
    } else if (e.key === 'ArrowLeft'){
      const inputs = document.getElementsByClassName('focusable');
      const selectedIndex = _.findIndex(inputs, (input) => input === focusedElement);

      if (selectedIndex >= 0){
        if (inputs[selectedIndex -1]){
          (inputs[selectedIndex -1] as HTMLElement)?.focus();
        } else {
          (inputs[inputs.length -1] as HTMLElement)?.focus();
        }
      } else if (document.activeElement.className.includes('--stake')){
        (inputs[1] as HTMLElement)?.focus();
      }
    }

  }, [bets, focusedElement, stake]);

  useEffect(() => {
    addEventListener('keydown', tabFunction);
    return () => {
      removeEventListener('keydown', tabFunction);
    };
  }, [bets, focusedElement, stake]);

  const handleClick = (focusValue) => {
    setFocus(focusValue);
    setShowModal(!showModal);
  };

  const getTitle = () => {
    let title = t('typeCodes.cardCodesLottoTitle');

    if (focus === 'betCodes'){
      title = t('typeCodes.betCodesLottoTitle');
    }

    return title;
  };

  const checkOptions = (type) => {
    if (type === 'eventCodes'){
      const newCodes = _.map(cardCodes, (codes) => {
        if (typeOfCode === 'cardCode'){
          return { code: (codes.couponCardCode).toString(), name: codes.name };
        } else {
          return { code: (codes.couponCode).toString(), name: codes.name };
        }
      });
      return newCodes;
    } else {
      if (type === 'marketCodes'){
        const newCodes = _.map(betCodes, (codes) => {
          if (typeOfCode === 'cardCode'){
            return { code: (codes.betCardCode).toString(),
              name: checkMarketName(codes.marketType, codes.specValue1, codes.specifier1, codes.specValue2, codes.specifier2) };
          } else {
            return { code: (codes.betCode).toString(),
              name: checkMarketName(codes.marketType, codes.specValue1, codes.specifier1, codes.specValue2, codes.specifier2) };
          }
        });
        return newCodes;
      }
    }
    return null;
  };

  useEffect(() => {
    firstFocus.current.focus();
  }, [firstFocus]);

  const changeClear = useCallback(() => {
    setClear(false);
  }, []);

  const changeLastSelection = useCallback(() => {
    setLastSelection(false);
  }, []);

  const SmartInputSelect = useCallback((value, type) => {
    dispatch.lotto.changeValues({ key: type, value });
    if (type === 'betCode'){
      const event = _.find(cardCodes, { [typeOfCode === 'cardCode' ? 'couponCode' : 'couponCardCode']: typeOfCode === 'cardCode' ? +value : value });
      if (event){
        dispatch.lotto.getNumbersColours({ tournamentCode: event.code });
      }
    }
    if (type === 'betType' && value){
      const market = _.find(betCodes, { [typeOfCode === 'cardCode' ? 'betCardCode' : 'betCode']: typeOfCode === 'cardCode' ? value : +value });
      if (market){
        setSelectedMarket(checkMarketName(market.marketType, market.specValue1, market.specifier1, market.specValue2, market.specifier2));
      } else {
        setSelectedMarket(null);
      }
    } else if (type === 'betType' && !value){
      setSelectedMarket(null);
    }
  }, [betCodes, selectedMarket, dispatch.lotto.changeValues]);

  const changeLastValues = useCallback((values) => {
    setLastValues(values);
  }, []);

  const markets = checkOptions('marketCodes');

  const changeMarketDefault = useCallback((value) => {
    const market = _.find(markets, { name: value });
    SmartInputSelect(market.code, 'betType');
    setMarketDefaultValue(value);
  }, [markets, dispatch.lotto.changeValues]);

  return (
    <>
      <form autoComplete="off" onBlur={ () => setShouldScroll(true) } onFocus={ () => setShouldScroll(false) }>
        <div className="fast-mode__form">
          <div className="fast-mode__label fast-mode__underline" onClick={ () => handleClick('cardCodes') }>
            { t('betCodeSearch.cardCode') }
          </div>
          <div className="fast-mode__label fast-mode__underline" onClick={ () => handleClick('betCodes') }>
            { t('racingDetail.retailBetCodeLabel') }
          </div>
          <div className="fast-mode__label">
            { t('racingDetail.retailStake') }
          </div>
          <div />
          <SmartInput
            options={ checkOptions('eventCodes') }
            onSelect={ SmartInputSelect }
            className="fast-mode__search-input focusable fast-mode__search-input--cardCodes"
            ref={ firstFocus }
            name="betCode"
            onFocus={ () => setFocus('cardCodes') }
            placeHolder={ t(`typeOfCode.${ [typeOfCode] }`) }
            defaultValue={ defaultValue }
            isFocused={ focus === 'cardCodes' && !shouldScroll }
            shouldOpen={ (focusedElement.className).includes('fast-mode__search-input--cardCodes') }
          />
          <SmartInput
            options={ checkOptions('marketCodes') }
            onSelect={ SmartInputSelect }
            className="fast-mode__search-input focusable fast-mode__search-input--betcodes"
            name="betType"
            ref={ secondFocus }
            onFocus={ () => setFocus('betCodes') }
            placeHolder={ typeOfCode === 'cardCode' ? t('numbers.betCodePlaceholder') : t('numbers.betCouponCodePlaceholder') }
            defaultValue={ marketDefaultValue }
            isFocused={ focus === 'betCodes' }
            shouldOpen={ (focusedElement.className).includes('fast-mode__search-input--betcodes') && !shouldScroll }
          />
          <input
            className="fast-mode__input fast-mode__input--stake"
            name="stake"
            ref={ stakeRef }
            value={ stake }
            onChange={ handleStake }
          />
          <button
            type="button"
            onClick={ handleLottoSubmit }
            className={
              classNames({
                'fast-mode__button' : true,
                'fast-mode__button--disabled': haveEmptyFields()
              })
            }
            disabled = { haveEmptyFields() }
          >
            <Icon
              icon="return"
              className={
                classNames({
                  'fast-mode__check': true,
                  'fast-mode__check--disabled': haveEmptyFields()
                })
              }
            />
          </button>
        </div>
        <div className="fast-mode__input-wrapper fast-mode__input-wrapper--lotto">
        </div>
        <div className="fast-mode__numbers-input">
          <div className="fast-mode__label">
            <span  className="fast-mode__tooltip">
              { t('numbers.numbers') }
              <ToolTip
                title={ null }
                formattedMessage={
                  <Trans i18nKey="numbers.inputNumbersInfo">
                    <strong></strong>
                  </Trans>
                }
                className="fast-mode__tooltip"
                type="big"
              />
            </span>
          </div>
          <NumberInput
            ref={ lastFocus }
            selectedMarket={ selectedMarket }
            clear={ clear }
            setClear={ changeClear }
            lastSelection={ lastSelection }
            shouldListen={ !shouldScroll }
            setLastSelection={ changeLastSelection }
            numberColours={ checkColors() }
            lastValues={ lastValues }
          />
        </div>
        {
          _.some(lottoErrors, (error) => error !== '') &&
          <div className="fast-mode__errors-container">
            <span className="fast-mode__error">
              { `${ lottoErrors.betcode || '' } ${ lottoErrors.betType || '' } ${ lottoErrors.numbers || '' } ${ lottoErrors.stake || '' }` }
            </span>
          </div>
        }
        <FastModeHistory smartInputSelect={ changeMarketDefault } changeLastValues={ changeLastValues } />
      </form>
      {
        showModal &&
        <DraggableDialog className="fast-mode__dialog" placement="right-start">
          <DraggableDialog.Content>
            <TypeCodes
              closeModal = { () => setShowModal(false) }
              title= { getTitle() }
              changeFocus= { changeFocus }
            >
              { focus === 'cardCodes' && <TypeCodes.CardCodes productType={ 4 } /> }
              { (focus !== 'cardCodes') && <TypeCodes.NumberCodes /> }
            </TypeCodes>
          </DraggableDialog.Content>
        </DraggableDialog>
      }
    </>
  );
}

export default NumbersFastMode;
